import { useState, useEffect, useMemo } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useCatalog } from '../../hooks/useSource';
import { sourceService } from '../../services/source.service';
import type { CatalogStream } from '../../services/source.service';
import { summarizeNumber } from '../../services/formatting.service';
import BackgroundService from '../../services/bg.service';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Checkbox } from '../ui/checkbox';
import { Badge } from '../ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { ArrowLeft, ChevronDown, ChevronRight, Database, Loader2, RefreshCw, Search, Settings, Wand2 } from 'lucide-react';
import { toast } from 'sonner';

// Per-stream config for meltano_streams mode
interface MeltanoStreamConfig {
  selected: boolean;
  replication_method: string;
  replication_key: string | null;
  selected_columns?: string[] | null; // null/undefined = all columns
}

type MeltanoStreamsValue = Record<string, MeltanoStreamConfig>;

// Legacy mode props
interface LegacyProps {
  mode?: 'legacy';
  currentStreams: string[];
  onConfirm: (streams: string[]) => void;
  value?: never;
  onSave?: never;
}

// Meltano streams mode props
interface MeltanoStreamsProps {
  mode: 'meltano_streams';
  value: string;
  onSave: (value: string) => void;
  currentStreams?: never;
  onConfirm?: never;
}

type StreamSelectorDialogProps = {
  sourceId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
} & (LegacyProps | MeltanoStreamsProps);

export function StreamSelectorDialog(props: StreamSelectorDialogProps) {
  const { sourceId, open, onOpenChange } = props;
  const mode = props.mode || 'legacy';

  const queryClient = useQueryClient();
  const { data: catalogData, isLoading: catalogLoading } = useCatalog(sourceId, open);

  // Legacy mode state
  const [selected, setSelected] = useState<Set<string>>(new Set());
  // Meltano streams mode state
  const [msConfig, setMsConfig] = useState<MeltanoStreamsValue>({});
  const [expandedStream, setExpandedStream] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [discovering, setDiscovering] = useState(false);

  // Column configuration view state
  const [columnConfigStream, setColumnConfigStream] = useState<string | null>(null);
  const [columnSearch, setColumnSearch] = useState('');
  const [suggestingColumns, setSuggestingColumns] = useState(false);
  const [saving, setSaving] = useState(false);

  // Initialize state when dialog opens
  useEffect(() => {
    if (!open) return;
    setSearch('');
    setExpandedStream(null);
    setColumnConfigStream(null);
    setColumnSearch('');

    if (mode === 'legacy') {
      const legacyProps = props as LegacyProps;
      setSelected(new Set(legacyProps.currentStreams.filter(Boolean)));
    } else {
      // Parse existing meltano_streams JSON
      const mProps = props as MeltanoStreamsProps;
      try {
        const parsed = JSON.parse(mProps.value || '{}');
        if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
          setMsConfig(parsed as MeltanoStreamsValue);
        } else {
          setMsConfig({});
        }
      } catch {
        setMsConfig({});
      }
    }
  }, [open]);

  // Auto-attach to an active discovery job when dialog opens
  useEffect(() => {
    if (!open || catalogLoading || discovering) return;
    const activeJobId = catalogData?.active_discovery_job_id;
    if (!activeJobId) return;

    setDiscovering(true);
    (async () => {
      try {
        await BackgroundService.getInstance().waitForJob(activeJobId);
        queryClient.invalidateQueries({ queryKey: ['sourceConfigOptions', sourceId] });
        let attempts = 0;
        const maxAttempts = 60;
        while (attempts < maxAttempts) {
          await new Promise((r) => setTimeout(r, 5000));
          const result = await sourceService.getCatalog(sourceId);
          if (result.has_catalog && !result.active_discovery_job_id) {
            queryClient.invalidateQueries({ queryKey: ['sourceCatalog', sourceId] });
            break;
          }
          attempts++;
        }
      } catch (err) {
        console.error(err);
        toast.error('Catalog discovery failed');
      } finally {
        setDiscovering(false);
      }
    })();
  }, [open, catalogLoading, catalogData?.active_discovery_job_id]);

  const streams = catalogData?.streams ?? [];
  const hasCatalog = catalogData?.has_catalog ?? false;

  const filteredStreams = useMemo(() => {
    if (!search) return streams;
    const lower = search.toLowerCase();
    return streams.filter((s) => s.stream.toLowerCase().includes(lower));
  }, [streams, search]);

  // Legacy mode helpers
  const allFilteredSelected = mode === 'legacy'
    ? filteredStreams.length > 0 && filteredStreams.every((s) => selected.has(s.stream))
    : filteredStreams.length > 0 && filteredStreams.every((s) => msConfig[s.stream]?.selected);

  const selectedCount = mode === 'legacy'
    ? selected.size
    : Object.values(msConfig).filter((c) => c.selected).length;

  const toggleStream = (stream: string) => {
    if (mode === 'legacy') {
      setSelected((prev) => {
        const next = new Set(prev);
        if (next.has(stream)) {
          next.delete(stream);
        } else {
          next.add(stream);
        }
        return next;
      });
    } else {
      setMsConfig((prev) => {
        const existing = prev[stream];
        if (existing) {
          return { ...prev, [stream]: { ...existing, selected: !existing.selected } };
        }
        // New entry: auto-generate defaults from catalog
        const catalogStream = streams.find((s) => s.stream === stream);
        return {
          ...prev,
          [stream]: {
            selected: true,
            replication_method: catalogStream?.replication_method || 'FULL_TABLE',
            replication_key: catalogStream?.replication_keys?.[0] || null,
          },
        };
      });
    }
  };

  const toggleAll = () => {
    if (mode === 'legacy') {
      setSelected((prev) => {
        const next = new Set(prev);
        if (allFilteredSelected) {
          for (const s of filteredStreams) next.delete(s.stream);
        } else {
          for (const s of filteredStreams) next.add(s.stream);
        }
        return next;
      });
    } else {
      setMsConfig((prev) => {
        const next = { ...prev };
        if (allFilteredSelected) {
          for (const s of filteredStreams) {
            if (next[s.stream]) {
              next[s.stream] = { ...next[s.stream], selected: false };
            }
          }
        } else {
          for (const s of filteredStreams) {
            if (next[s.stream]) {
              next[s.stream] = { ...next[s.stream], selected: true };
            } else {
              next[s.stream] = {
                selected: true,
                replication_method: s.replication_method || 'FULL_TABLE',
                replication_key: s.replication_keys?.[0] || null,
              };
            }
          }
        }
        return next;
      });
    }
  };

  const handleDiscover = async () => {
    setDiscovering(true);
    try {
      const { job_id } = await sourceService.discoverCatalog(sourceId);
      await BackgroundService.getInstance().waitForJob(job_id);
      // Some connectors populate an OPTIONS_MULTISELECT field's choices (e.g.
      // an account/property picker) during this same discovery run, before a
      // stream catalog can even be built — refresh those regardless of
      // whether a catalog shows up below.
      queryClient.invalidateQueries({ queryKey: ['sourceConfigOptions', sourceId] });

      let attempts = 0;
      const maxAttempts = 60;
      while (attempts < maxAttempts) {
        await new Promise((r) => setTimeout(r, 5000));
        const result = await sourceService.getCatalog(sourceId);
        if (result.has_catalog) {
          queryClient.invalidateQueries({ queryKey: ['sourceCatalog', sourceId] });
          break;
        }
        attempts++;
      }
      if (attempts >= maxAttempts) {
        toast.error('No stream catalog yet — if this connector needs additional setup (e.g. selecting an account/property above), configure it and run discovery again.');
      }
    } catch (err) {
      console.error(err);
      toast.error('Catalog discovery failed');
    } finally {
      setDiscovering(false);
    }
  };

  const handleConfirm = async () => {
    if (mode === 'legacy') {
      (props as LegacyProps).onConfirm(Array.from(selected));
      onOpenChange(false);
      return;
    }

    setSaving(true);
    try {
      // Update local state via parent callback
      (props as MeltanoStreamsProps).onSave(JSON.stringify(msConfig));

      // Single call: saves to MongoDB + updates catalog.json on S3
      await sourceService.saveCatalogSelections(sourceId, msConfig);

      toast.success('Stream configuration saved');
      queryClient.invalidateQueries({ queryKey: ['source', sourceId] });
      onOpenChange(false);
    } catch (err) {
      console.error('Failed to save stream configuration:', err);
      toast.error('Failed to save stream configuration');
    } finally {
      setSaving(false);
    }
  };

  const updateStreamConfig = (stream: string, updates: Partial<MeltanoStreamConfig>) => {
    setMsConfig((prev) => ({
      ...prev,
      [stream]: { ...prev[stream], ...updates },
    }));
  };

  // Column configuration helpers
  const activeColumnStream = columnConfigStream ? streams.find((s) => s.stream === columnConfigStream) : null;
  const activeStreamConfig = columnConfigStream ? msConfig[columnConfigStream] : null;

  const getSelectedColumnsForStream = (streamName: string): Set<string> => {
    const config = msConfig[streamName];
    if (!config?.selected_columns) {
      // All columns selected by default
      const catalogStream = streams.find((s) => s.stream === streamName);
      return new Set(catalogStream?.columns.map((c) => c.name) ?? []);
    }
    return new Set(config.selected_columns);
  };

  const filteredColumns = useMemo(() => {
    if (!activeColumnStream) return [];
    const cols = activeColumnStream.columns;
    if (!columnSearch) return cols;
    const lower = columnSearch.toLowerCase();
    return cols.filter((c) => c.name.toLowerCase().includes(lower));
  }, [activeColumnStream, columnSearch]);

  const isPrimaryKey = (streamName: string, colName: string): boolean => {
    const catalogStream = streams.find((s) => s.stream === streamName);
    return catalogStream?.primary_keys?.includes(colName) ?? false;
  };

  const toggleColumn = (streamName: string, colName: string) => {
    if (isPrimaryKey(streamName, colName)) return; // Can't deselect primary keys

    const currentSelected = getSelectedColumnsForStream(streamName);
    const next = new Set(currentSelected);
    if (next.has(colName)) {
      next.delete(colName);
    } else {
      next.add(colName);
    }
    updateStreamConfig(streamName, { selected_columns: Array.from(next) });
  };

  const toggleAllColumns = (streamName: string) => {
    const catalogStream = streams.find((s) => s.stream === streamName);
    if (!catalogStream) return;

    const currentSelected = getSelectedColumnsForStream(streamName);
    const visibleCols = filteredColumns;
    const allVisible = visibleCols.every((c) => currentSelected.has(c.name));

    const next = new Set(currentSelected);
    if (allVisible) {
      // Deselect all visible (except primary keys)
      for (const c of visibleCols) {
        if (!isPrimaryKey(streamName, c.name)) {
          next.delete(c.name);
        }
      }
    } else {
      // Select all visible
      for (const c of visibleCols) {
        next.add(c.name);
      }
    }
    updateStreamConfig(streamName, { selected_columns: Array.from(next) });
  };

  const resetColumnsToAll = (streamName: string) => {
    updateStreamConfig(streamName, { selected_columns: null });
  };

  const handleSuggestColumns = async (streamName: string) => {
    setSuggestingColumns(true);
    try {
      const catalogStream = streams.find((s) => s.stream === streamName);
      if (!catalogStream) return;

      // Gather context: selected tables and their columns
      const selectedTables = Object.entries(msConfig)
        .filter(([name, cfg]) => cfg.selected && name !== streamName)
        .map(([name]) => {
          const s = streams.find((st) => st.stream === name);
          return {
            stream: name,
            columns: s?.columns.map((c) => c.name) ?? [],
            primary_keys: s?.primary_keys ?? [],
          };
        });

      const result = await sourceService.suggestColumns(sourceId, {
        stream: streamName,
        columns: catalogStream.columns.map((c) => ({ name: c.name, type: c.type })),
        primary_keys: catalogStream.primary_keys,
        other_selected_streams: selectedTables,
      });

      if (result.suggested_columns && result.suggested_columns.length > 0) {
        updateStreamConfig(streamName, { selected_columns: result.suggested_columns });
        toast.success(`Selected ${result.suggested_columns.length} of ${catalogStream.columns.length} columns`);
      }
    } catch (err) {
      console.error(err);
      toast.error('Failed to get column suggestions');
    } finally {
      setSuggestingColumns(false);
    }
  };

  const columnSelectedCount = activeColumnStream && columnConfigStream
    ? getSelectedColumnsForStream(columnConfigStream).size
    : 0;

  const allFilteredColumnsSelected = activeColumnStream && columnConfigStream
    ? filteredColumns.length > 0 && filteredColumns.every((c) => getSelectedColumnsForStream(columnConfigStream).has(c.name))
    : false;

  const renderStreamRow = (s: CatalogStream) => {
    const isSelected = mode === 'legacy' ? selected.has(s.stream) : !!msConfig[s.stream]?.selected;
    const isExpanded = mode === 'meltano_streams' && expandedStream === s.stream;
    const config = msConfig[s.stream];
    const hasColumnConfig = config?.selected_columns != null;
    const colCount = hasColumnConfig ? config.selected_columns!.length : s.columns.length;

    return (
      <div key={s.stream} className="border-b last:border-b-0">
        <label
          className="flex items-center gap-2 px-3 py-2 hover:bg-accent/50 cursor-pointer"
        >
          <Checkbox
            checked={isSelected}
            onCheckedChange={() => toggleStream(s.stream)}
          />
          <span className="text-sm flex-1 truncate">{s.stream}</span>
          {s.row_count != null && (
            <Badge
              variant="outline"
              className={`text-xs shrink-0 ${
                s.row_count >= 1_000_000
                  ? 'border-amber-500 text-amber-700 dark:text-amber-400'
                  : 'text-muted-foreground'
              }`}
              title={`~${s.row_count.toLocaleString()} rows (approximate)`}
            >
              ~{summarizeNumber(s.row_count)} rows
            </Badge>
          )}
          <Badge variant={hasColumnConfig ? 'default' : 'outline'} className="text-xs shrink-0">
            {colCount}{hasColumnConfig ? `/${s.columns.length}` : ''} cols
          </Badge>
          {mode === 'meltano_streams' && isSelected && (
            <>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 shrink-0"
                title="Configure columns"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setColumnSearch('');
                  setColumnConfigStream(s.stream);
                }}
              >
                <Settings className="h-3.5 w-3.5" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="h-6 w-6 p-0 shrink-0"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setExpandedStream(isExpanded ? null : s.stream);
                }}
              >
                {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              </Button>
            </>
          )}
        </label>

        {mode === 'meltano_streams' && isExpanded && config && (
          <div className="px-8 pb-3 space-y-3 bg-muted/30">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Replication Method</label>
                <Select
                  value={config.replication_method || 'FULL_TABLE'}
                  onValueChange={(val) => updateStreamConfig(s.stream, { replication_method: val })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="FULL_TABLE">Full Table</SelectItem>
                    <SelectItem value="INCREMENTAL">Incremental</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <label className="text-xs font-medium text-muted-foreground">Replication Key</label>
                <Select
                  value={config.replication_key || '__none__'}
                  onValueChange={(val) => updateStreamConfig(s.stream, { replication_key: val === '__none__' ? null : val })}
                >
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue placeholder="None" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__none__">None</SelectItem>
                    {s.replication_keys?.length > 0 && s.replication_keys.map((key) => (
                      <SelectItem key={key} value={key}>{key} (recommended)</SelectItem>
                    ))}
                    {s.columns
                      .map((c) => c.name)
                      .filter((name) => !s.replication_keys?.includes(name))
                      .map((key) => (
                        <SelectItem key={key} value={key}>{key}</SelectItem>
                      ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  };

  // Column configuration view
  const renderColumnConfig = () => {
    if (!activeColumnStream || !columnConfigStream) return null;

    const selectedCols = getSelectedColumnsForStream(columnConfigStream);
    const hasCustomSelection = activeStreamConfig?.selected_columns != null;

    return (
      <>
        <DialogHeader>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 w-7 p-0"
              onClick={() => setColumnConfigStream(null)}
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex-1 min-w-0">
              <DialogTitle className="text-base truncate">{columnConfigStream}</DialogTitle>
              <DialogDescription className="text-xs">
                Select which columns to sync for this stream.
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-3 px-6">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Filter columns..."
                value={columnSearch}
                onChange={(e) => setColumnSearch(e.target.value)}
                className="pl-8"
              />
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => handleSuggestColumns(columnConfigStream)}
              disabled={suggestingColumns}
              title="AI suggest columns for data warehouse"
            >
              {suggestingColumns ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <Wand2 className="h-4 w-4" />
              )}
            </Button>
            {hasCustomSelection && (
              <Button
                variant="ghost"
                size="sm"
                className="text-xs"
                onClick={() => resetColumnsToAll(columnConfigStream)}
              >
                Reset
              </Button>
            )}
          </div>

          <div className="flex items-center gap-2 px-1">
            <Checkbox
              id="select-all-cols"
              checked={allFilteredColumnsSelected}
              onCheckedChange={() => toggleAllColumns(columnConfigStream)}
            />
            <label htmlFor="select-all-cols" className="text-sm font-medium cursor-pointer">
              Select All ({filteredColumns.length})
            </label>
            <Badge variant="secondary" className="ml-auto text-xs">
              {columnSelectedCount}/{activeColumnStream.columns.length} selected
            </Badge>
          </div>

          <div className="border rounded-md max-h-96 overflow-y-auto">
            {filteredColumns.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No columns match your filter.</p>
            ) : (
              filteredColumns.map((col) => {
                const isColSelected = selectedCols.has(col.name);
                const isPK = isPrimaryKey(columnConfigStream, col.name);
                const isRepKey = activeStreamConfig?.replication_key === col.name;

                return (
                  <label
                    key={col.name}
                    className="flex items-center gap-2 px-3 py-1.5 hover:bg-accent/50 cursor-pointer border-b last:border-b-0"
                  >
                    <Checkbox
                      checked={isColSelected}
                      disabled={isPK}
                      onCheckedChange={() => toggleColumn(columnConfigStream, col.name)}
                    />
                    <span className="text-sm flex-1 truncate font-mono text-xs">{col.name}</span>
                    {isPK && (
                      <Badge variant="default" className="text-[10px] px-1.5 py-0 shrink-0">PK</Badge>
                    )}
                    {isRepKey && (
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0 shrink-0">REP</Badge>
                    )}
                    <span className="text-[10px] text-muted-foreground shrink-0">{col.type}</span>
                  </label>
                );
              })
            )}
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => setColumnConfigStream(null)}>
            Back to Streams
          </Button>
        </DialogFooter>
      </>
    );
  };

  // Stream list view
  const renderStreamList = () => (
    <>
      <DialogHeader>
        <DialogTitle>Select Streams</DialogTitle>
        <DialogDescription>
          Choose which streams to sync from this source.
        </DialogDescription>
      </DialogHeader>

      {discovering ? (
        <div className="flex flex-col items-center gap-4 py-14 px-6">
          <div className="rounded-full bg-muted p-3">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
          <div className="text-center space-y-1">
            <p className="text-sm font-medium">Discovering streams</p>
            <p className="text-sm text-muted-foreground">
              Connecting to the source and fetching the schema. This can take a few minutes.
            </p>
          </div>
        </div>
      ) : catalogLoading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          <span className="ml-2 text-sm text-muted-foreground">Loading catalog...</span>
        </div>
      ) : !hasCatalog ? (
        <div className="flex flex-col items-center gap-4 py-10 px-6">
          <div className="rounded-full bg-muted p-3">
            <Database className="h-6 w-6 text-muted-foreground" />
          </div>
          <div className="text-center space-y-1">
            <p className="text-sm font-medium">No catalog found</p>
            <p className="text-sm text-muted-foreground">
              Run discovery to connect to the source and fetch available streams.
            </p>
          </div>
          <Button onClick={handleDiscover} size="sm">
            <Search className="h-4 w-4 mr-2" />
            Discover Streams
          </Button>
        </div>
      ) : (
        <div className="space-y-3 px-6">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Filter streams..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-8"
              />
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleDiscover}
              title="Re-discover streams"
            >
              <RefreshCw className="h-4 w-4" />
            </Button>
          </div>

          <div className="flex items-center gap-2 px-1">
            <Checkbox
              id="select-all"
              checked={allFilteredSelected}
              onCheckedChange={toggleAll}
            />
            <label htmlFor="select-all" className="text-sm font-medium cursor-pointer">
              Select All ({filteredStreams.length})
            </label>
            {selectedCount > 0 && (
              <Badge variant="secondary" className="ml-auto text-xs">
                {selectedCount} selected
              </Badge>
            )}
          </div>

          <div className="border rounded-md max-h-96 overflow-y-auto">
            {filteredStreams.length === 0 ? (
              <p className="text-sm text-muted-foreground text-center py-4">No streams match your filter.</p>
            ) : (
              filteredStreams.map((s: CatalogStream) => renderStreamRow(s))
            )}
          </div>
        </div>
      )}

      {!discovering && (
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          {hasCatalog && (
            <Button onClick={handleConfirm} disabled={saving}>
              {saving && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              Apply ({selectedCount})
            </Button>
          )}
        </DialogFooter>
      )}
    </>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl w-[calc(100%-2rem)]">
        {columnConfigStream ? renderColumnConfig() : renderStreamList()}
      </DialogContent>
    </Dialog>
  );
}
